import React from 'react';
import { get, isEmpty } from 'lodash';

// React view component to render
import { memberAddEditBillingAddress as MemberAddEditBillingAddressView } from 'ui/views/widgets/memberAddEditBillingAddress';
import { MemberAddEditAddressService } from 'services/memberAddEditAddress';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as pageLevelPreloader from 'modules/page-level-preloader';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberAddEditBillingAddress } from 'ui/containers/MemberAddEditBillingAddress';
 *
 * // render
 * <memberAddEditBillingAddress propName='propValue'/>
 */
export class MemberAddEditBillingAddress extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // create service instance
        this.memberAddEditAddressService = new MemberAddEditAddressService();

        this.state = {
            isFetching: false,
        };

        // bind events and methods
        this.__bind();
    }

    /**
     * @desc Bind events and methods to the instance.
     */
    __bind() {
        this.handleSetDefaultAction = this.handleSetDefaultAction.bind( this );
        this.initiateAddEditAddress = this.initiateAddEditAddress.bind( this );
        this.handleSelection = this.handleSelection.bind( this );
        this.handleDeleteAction = this.handleDeleteAction.bind( this );
        this.handleInactivateAction = this.handleInactivateAction.bind( this );
        this.handleActivateAction = this.handleActivateAction.bind( this );
    }

    /*****************************************/

    async initiateAddEditAddress( { value, type, item = {}, sectionName = null } ) {
        log.debug( 'MemberAddEditBillingAddress.initiateAddEditAddress()' );

        // cancel ongoing request
        this.memberAddEditAddressService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.memberAddEditAddressService.fetchAddEditAddressForm( {
            url: value.url,
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.ADD_EDIT_ADDRESS,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...data,
                    actionType: type,
                    itemData: item,
                    onCancelClick: () => {
                        overlay.close();
                    },
                    onSumbitClick: async ( { formData } ) => {
                        overlay.close();
                        this.props.onAddEditAddressClick( { type, address: formData, id: get( item, 'id', null ), sectionName  } );
                    },
                }
            } );
        }
    }

    /**
     * @desc: action executed when user makes a selection default
     */
    handleSetDefaultAction( { address, sectionName = null } ) {
        log.debug( 'MemberAddEditBillingAddress.handleSetDefaultAction()' );

        if ( !isEmpty( this.props.defaultDescription ) ) {
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.CHANGE_DEFAULT_CONFIRM,
                    showCloseButton: false,
                    ignoreOutsideClick: false,
                },
                config: {},
                data: {
                    ...this.props.changeConfirmation,
                    onSumbitClick: async ( ) => {
    
                        overlay.close();
                        this.props.onDefaultSelection( { address, sectionName } );
    
                    },
                    onCancelClick: () => {
                        overlay.close();
                    },
                },
            } );
        } else {
            this.props.onDefaultSelection( { address, sectionName } );
        }
    }

    /**
     * @desc: action executed when user selects an address
     */
    handleSelection( { address } ) {
        log.debug( 'MemberAddEditBillingAddress.handleSelection()' );
        this.props.onAddressSelection( { address } );
    }

    /**
     * @desc: action executed when MSR clicks on delete action
     */
    handleDeleteAction( { address, sectionName = null } ) {
        log.debug( 'MemberAddEditBillingAddress.handleDeleteAction()' );
        this.props.onAddressDelete( { address, sectionName } );
    }

    /**
     * @desc: action executed when MSR clicks on inactivate action
     */
    handleInactivateAction( { address, selected, sectionName = null } ) {
        log.debug( 'MemberAddEditBillingAddress.handleInactivateAction()' );
        this.props.onAddressInactivate( { address, selected, sectionName } );
    }

    /**
     * @desc: action executed when MSR clicks on activate action
     */
    handleActivateAction( { address, sectionName = null } ) {
        log.debug( 'MemberAddEditBillingAddress.handleActivateAction()' );
        this.props.onAddressActivate( { address, sectionName } );
    }

    /*****************************************/

     /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.memberAddEditAddressService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberAddEditBillingAddressView
                { ...this.state }
                { ...this.props }
                onAddEditActionClick={ this.initiateAddEditAddress }
                onSetDefaultClick={ this.handleSetDefaultAction }
                onSelectActionClick={ this.handleSelection }
                onDeleteActionClick={ this.handleDeleteAction }
                onActivateActionClick={ this.handleActivateAction }
                onInactivateActionClick={ this.handleInactivateAction }
            />
        );
    }
}

// set display name
MemberAddEditBillingAddress.displayName = 'MemberAddEditBillingAddress';

// set default props
MemberAddEditBillingAddress.defaultProps = {};

// prop types
MemberAddEditBillingAddress.propTypes = {};
